import { Catalog } from 'models/knowledgeBase/Catalog';
import { Document, DocumentStatus, PreProcessConfig } from 'models/knowledgeBase/Document';
import { PaginatedList } from 'models/shared/PaginatedList';
import { BlipApi } from './BlipApi';
import axios from 'axios';
import { getFileBinary } from 'utils/fileUtil';

export class UploadError extends Error {
  constructor(public readonly failedFiles: string[]) {
    super(`Failed to upload file(s): ${failedFiles.join(', ')}`);
    this.name = 'UploadError';
  }
}

export class KnowledgeBaseApi {
  private static readonly AIPLATFORM_POSTMASTER = 'postmaster@aiplatform.msging.net';

  //#region Helper Methods

  /**
   * Creates a Document instance from API response item
   */
  private static createDocumentFromResponse(item: Record<string, unknown>): Document {
    return new Document({
      id: item.id as string,
      name: item.name as string,
      url: (item.url as string) || '',
      extension: (item.extension as string) || '',
      updated_at: (item.updated_at as string) || (item.updatedAt as string),
      updated_by: (item.updated_by as string) || (item.updatedBy as string),
      status:
        (item.status as Document['status']) || (item.syncStatus as Document['status']) || DocumentStatus.PROCESSING,
      content_size_bytes: item.content_size_bytes as number,
      markdown_content: item.markdown_content as string,
      version_id: item.version_id as string,
      catalog_id: item.catalog_id as string,
      versions: Array.isArray(item.versions)
        ? (item.versions as Record<string, unknown>[]).map((versionItem) =>
          this.createDocumentFromResponse(versionItem),
        )
        : [],
    });
  }

  /**
   * Extracts file extension from filename
   */
  private static getFileExtension(filename: string): string {
    return filename.split('.').pop() ?? '';
  }

  /**
   * Parses URL and returns base URL and paths for regex filters
   */
  private static parseUrlForFilters(url: string): { urlBase: string; paths: string[] } {
    const urlObj = new URL(url);
    const urlBase = `${urlObj.protocol}//${urlObj.host}`;
    const paths = urlObj.pathname !== '/' ? [`.*${urlObj.pathname}.*`] : [];
    return { urlBase, paths };
  }

  //#endregion

  //#region Catalog

  public static async getCatalogAsync(
    tenantId: string,
    take: number,
    skip: number,
    name?: string,
  ): Promise<PaginatedList<Catalog>> {
    const filter = name ? `&$filter=(name%20eq%20'${name}')` : '';

    const catalog = await BlipApi.sendCommand({
      to: this.AIPLATFORM_POSTMASTER,
      method: 'get',
      uri: `/knowledge-base/${tenantId}/catalog?$take=${take}&$skip=${skip}${filter}`,
    });
    return { items: catalog.response.items, total: catalog.response.total } as PaginatedList<Catalog>;
  }

  public static async getCatalogByIdAsync(tenantId: string, catalogId: string): Promise<Catalog> {
    const catalog = await BlipApi.sendCommand({
      to: this.AIPLATFORM_POSTMASTER,
      method: 'get',
      uri: `/knowledge-base/${tenantId}/catalog/${catalogId}`,
    });
    return new Catalog(catalog.response);
  }

  public static async createCatalogAsync(tenantId: string, name: string): Promise<Catalog> {
    const catalog = await BlipApi.sendCommand({
      to: this.AIPLATFORM_POSTMASTER,
      method: 'set',
      uri: `/knowledge-base/${tenantId}/catalog`,
      type: 'application/vnd.iris.aiplatform.knowledgebase-catalog+json',
      resource: {
        name,
      },
    });
    return new Catalog(catalog.response);
  }

  public static async deleteCatalogAsync(tenantId: string, id: string): Promise<void> {
    await BlipApi.sendCommand({
      to: this.AIPLATFORM_POSTMASTER,
      method: 'delete',
      uri: `/knowledge-base/${tenantId}/catalog/${id}`,
    });
  }

  public static async updateCatalogAsync(tenantId: string, id: string, name: string): Promise<Catalog> {
    const catalog = await BlipApi.sendCommand({
      to: this.AIPLATFORM_POSTMASTER,
      method: 'merge',
      uri: `/knowledge-base/${tenantId}/catalog/${id}`,
      type: 'application/vnd.iris.aiplatform.knowledgebase-catalog+json',
      resource: {
        name,
      },
    });
    return new Catalog(catalog.response);
  }

  //#endregion

  //#region Documents

  public static async getDocumentsAsync(
    tenantId: string,
    catalogId: string,
    take: number,
    skip: number,
    name?: string,
  ): Promise<PaginatedList<Document>> {
    const filter = name ? `&$filter=(name%20eq%20'${name}')` : '';

    const documents = await BlipApi.sendCommand({
      to: this.AIPLATFORM_POSTMASTER,
      method: 'get',
      uri: `/knowledge-base/${tenantId}/catalog/${catalogId}/document?$take=${take}&$skip=${skip}${filter}`,
    });

    // Convert response items to Document objects
    const documentItems = documents.response.items.map((item: Record<string, unknown>) =>
      this.createDocumentFromResponse(item),
    );

    return { items: documentItems, total: documents.response.total } as PaginatedList<Document>;
  }

  public static async getDocumentByIdAsync(tenantId: string, catalogId: string, documentId: string): Promise<Document> {
    const document = await BlipApi.sendCommand({
      to: this.AIPLATFORM_POSTMASTER,
      method: 'get',
      uri: `/knowledge-base/${tenantId}/catalog/${catalogId}/document/${documentId}`,
    });

    const item = document.response;
    return this.createDocumentFromResponse(item);
  }

  public static async createPreprocessedDocuments(
    tenantId: string,
    catalogId: string,
    preProcessConfig: PreProcessConfig[],
  ): Promise<{ documents: Document[]; failedFiles: string[] }> {
    // Handle file uploads — allSettled lets every upload attempt before evaluating failures
    const uploadResults = await Promise.allSettled(
      preProcessConfig.map(async (doc) => {
        const mediaUri = await this.uploadFileAsync(doc.file);
        return {
          name: doc.file.name,
          url: mediaUri,
          extension: this.getFileExtension(doc.file.name),
          content_size_bytes: doc.file.size,
          use_context_agent: doc.contextualization,
          use_text_cleaner: doc.optimization,
          use_summary_agent: doc.indexing,
        };
      }),
    );

    const failedFiles = uploadResults
      .map((result, i) => ({ result, file: preProcessConfig[i].file.name }))
      .filter(({ result }) => result.status === 'rejected')
      .map(({ file }) => file);

    const succeeded = uploadResults
      .filter((r): r is PromiseFulfilledResult<typeof r extends PromiseFulfilledResult<infer V> ? V : never> => r.status === 'fulfilled')
      .map(({ value }) => value);

    if (succeeded.length === 0) {
      throw new UploadError(failedFiles);
    }

    // Send only the successfully uploaded files to the API
    const document = await BlipApi.sendCommand({
      to: this.AIPLATFORM_POSTMASTER,
      method: 'set',
      uri: `/knowledge-base/${tenantId}/catalog/${catalogId}/documents`,
      type: 'application/vnd.iris.aiplatform.knowledgebase-documents+json',
      resource: {
        documents: succeeded,
      },
    });

    if (!document.response?.documents) {
      throw new Error('Unexpected response from knowledge base API');
    }

    return {
      documents: document.response.documents.map((doc: Record<string, unknown>) => this.createDocumentFromResponse(doc)),
      failedFiles,
    };
  }

  public static async createDocumentAsync(tenantId: string, catalogId: string, file: File): Promise<Document> {
    // Handle file upload
    const mediaUri = await this.uploadFileAsync(file);
    const fileExtension = this.getFileExtension(file.name);
    const name = file.name;

    // Create document with the media URI
    const document = await BlipApi.sendCommand({
      to: this.AIPLATFORM_POSTMASTER,
      method: 'set',
      uri: `/knowledge-base/${tenantId}/catalog/${catalogId}/document`,
      type: 'application/vnd.iris.aiplatform.knowledgebase-document+json',
      resource: {
        name,
        url: mediaUri,
        extension: fileExtension,
        content_size_bytes: file.size,
      },
    });

    const item = document.response;
    return this.createDocumentFromResponse(item);
  }

  public static async addUrlDocumentAsync(tenantId: string, catalogId: string, url: string): Promise<Document> {
    // Split URL into base and paths
    const { urlBase, paths } = this.parseUrlForFilters(url);

    const document = await BlipApi.sendCommand({
      to: this.AIPLATFORM_POSTMASTER,
      method: 'set',
      uri: `/knowledge-base/${tenantId}/catalog/${catalogId}/url`,
      type: 'application/vnd.iris.aiplatform.knowledgebase-url+json',
      resource: {
        name: urlBase,
        url: url,
        reprocess: true,
        url_regex_filters: paths,
        generate_qa: false,
      },
    });

    const item = document.response;
    return this.createDocumentFromResponse(item);
  }

  public static async addUrlDocumentCrawlAsync(tenantId: string, catalogId: string, url: string): Promise<Document> {
    // Split URL into base and paths
    const { urlBase, paths } = this.parseUrlForFilters(url);

    const document = await BlipApi.sendCommand({
      to: this.AIPLATFORM_POSTMASTER,
      method: 'set',
      uri: `/knowledge-base/${tenantId}/catalog/${catalogId}/url/crawl`,
      type: 'application/vnd.iris.aiplatform.knowledgebase-url-crawl+json',
      resource: {
        name: urlBase,
        url: url,
        reprocess: true,
        url_regex_filters: paths,
        generate_qa: false,
        get_urls_with_cloud_scraper: true,
      },
    });

    const item = document.response;
    return this.createDocumentFromResponse(item);
  }

  public static async updateUrlDocumentAsync(
    tenantId: string,
    catalogId: string,
    documentId: string,
    url: string,
  ): Promise<Document> {
    // Split URL into base and paths
    const { urlBase, paths } = this.parseUrlForFilters(url);

    const document = await BlipApi.sendCommand({
      to: this.AIPLATFORM_POSTMASTER,
      method: 'merge',
      uri: `/knowledge-base/${tenantId}/catalog/${catalogId}/url/${documentId}`,
      type: 'application/vnd.iris.aiplatform.knowledgebase-url+json',
      resource: {
        name: urlBase,
        url: url,
        reprocess: true,
        url_regex_filters: paths,
        generate_qa: false,
      },
    });

    const item = document.response;
    return this.createDocumentFromResponse(item);
  }

  public static async updateUrlDocumentCrawlAsync(
    tenantId: string,
    catalogId: string,
    documentId: string,
    url: string,
  ): Promise<Document> {
    // Split URL into base and paths
    const { urlBase, paths } = this.parseUrlForFilters(url);

    const document = await BlipApi.sendCommand({
      to: this.AIPLATFORM_POSTMASTER,
      method: 'merge',
      uri: `/knowledge-base/${tenantId}/catalog/${catalogId}/url/${documentId}/crawl`,
      type: 'application/vnd.iris.aiplatform.knowledgebase-url-crawl+json',
      resource: {
        name: urlBase,
        url: url,
        reprocess: true,
        url_regex_filters: paths,
        generate_qa: false,
        get_urls_with_cloud_scraper: true,
      },
    });

    const item = document.response;
    return this.createDocumentFromResponse(item);
  }

  public static async updateDocumentAsync(
    tenantId: string,
    catalogId: string,
    documentId: string,
    updateData: {
      version_id?: string;
      name?: string;
      status?: DocumentStatus;
    },
  ): Promise<Document> {
    const resource: { version_id?: string; name?: string; status?: DocumentStatus } = {};

    if (updateData.version_id !== undefined) resource.version_id = updateData.version_id;
    if (updateData.name !== undefined) resource.name = updateData.name;
    if (updateData.status !== undefined) resource.status = updateData.status;

    if (Object.keys(resource).length === 0) {
      throw new Error('At least one property must be provided for update');
    }

    const updatedDocument = await BlipApi.sendCommand({
      to: this.AIPLATFORM_POSTMASTER,
      method: 'merge',
      uri: `/knowledge-base/${tenantId}/catalog/${catalogId}/document/${documentId}`,
      type: 'application/vnd.iris.aiplatform.knowledgebase-document-update+json',
      resource,
    });

    return this.createDocumentFromResponse(updatedDocument.response);
  }

  public static async deleteDocumentAsync(tenantId: string, catalogId: string, documentId: string): Promise<void> {
    await BlipApi.sendCommand({
      to: this.AIPLATFORM_POSTMASTER,
      method: 'delete',
      uri: `/knowledge-base/${tenantId}/catalog/${catalogId}/document/${documentId}`,
    });
  }

  //#endregion

  //#region Document Versions

  public static async getDocumentVersionsAsync(
    tenantId: string,
    catalogId: string,
    documentId: string,
    take: number,
    skip: number,
    name?: string,
  ): Promise<PaginatedList<Document>> {
    const filter = name ? `&$filter=(name%20eq%20'${name}')` : '';

    const versions = await BlipApi.sendCommand({
      to: this.AIPLATFORM_POSTMASTER,
      method: 'get',
      uri: `/knowledge-base/${tenantId}/catalog/${catalogId}/document/${documentId}/version?$take=${take}&$skip=${skip}${filter}`,
    });

    // Convert response items to Document objects
    const versionItems = versions.response.items.map((item: Record<string, unknown>) =>
      this.createDocumentFromResponse(item),
    );

    return { items: versionItems, total: versions.response.total } as PaginatedList<Document>;
  }

  public static async createPreProcessedDocumentVersionAsync(
    tenantId: string,
    catalogId: string,
    documentId: string,
    preProcessConfig: PreProcessConfig,
  ): Promise<Document> {
    // Handle file upload
    const mediaUri = await this.uploadFileAsync(preProcessConfig.file);
    const fileExtension = this.getFileExtension(preProcessConfig.file.name);
    const name = preProcessConfig.file.name;

    // Create document with the media URI
    const document = await BlipApi.sendCommand({
      to: this.AIPLATFORM_POSTMASTER,
      method: 'set',
      uri: `/knowledge-base/${tenantId}/catalog/${catalogId}/document/${documentId}/replace`,
      type: 'application/vnd.iris.aiplatform.knowledgebase-document+json',
      resource: {
        name,
        url: mediaUri,
        extension: fileExtension,
        content_size_bytes: preProcessConfig.file.size,
        use_context_agent: preProcessConfig.contextualization,
				use_text_cleaner: preProcessConfig.optimization,
				use_summary_agent: preProcessConfig.indexing,
      },
    });

    if (!document.response) {
      throw new Error('Unexpected response from knowledge base API');
    }

    return this.createDocumentFromResponse(document.response);
  }

  public static async createDocumentVersionAsync(
    tenantId: string,
    catalogId: string,
    documentId: string,
    file: File,
  ): Promise<Document> {
    // Handle file upload
    const mediaUri = await this.uploadFileAsync(file);
    const fileExtension = this.getFileExtension(file.name);
    const name = file.name;

    // Create document with the media URI
    const document = await BlipApi.sendCommand({
      to: this.AIPLATFORM_POSTMASTER,
      method: 'set',
      uri: `/knowledge-base/${tenantId}/catalog/${catalogId}/document/${documentId}`,
      type: 'application/vnd.iris.aiplatform.knowledgebase-document+json',
      resource: {
        name,
        url: mediaUri,
        extension: fileExtension,
        content_size_bytes: file.size,
      },
    });

    const item = document.response;
    return this.createDocumentFromResponse(item);
  }

  public static async updateDocumentVersionAsync(
    tenantId: string,
    catalogId: string,
    documentId: string,
    versionId: string,
    updateData: {
      status?: DocumentStatus;
      name?: string;
    },
  ): Promise<Document> {
    const resource: {
      status?: DocumentStatus;
      name?: string;
    } = {};

    if (updateData.status !== undefined) {
      resource.status = updateData.status;
    }

    if (updateData.name !== undefined) {
      resource.name = updateData.name;
    }

    if (Object.keys(resource).length === 0) {
      throw new Error('At least one property (status or name) must be provided for update');
    }

    const updatedDocument = await BlipApi.sendCommand({
      to: this.AIPLATFORM_POSTMASTER,
      method: 'merge',
      uri: `/knowledge-base/${tenantId}/catalog/${catalogId}/document/${documentId}/version/${versionId}`,
      type: 'application/vnd.iris.aiplatform.knowledgebase-document-version-update+json',
      resource,
    });

    const item = updatedDocument.response;
    return this.createDocumentFromResponse(item);
  }

  public static async deleteDocumentVersionAsync(
    tenantId: string,
    catalogId: string,
    documentId: string,
    versionId: string,
  ): Promise<void> {
    await BlipApi.sendCommand({
      to: this.AIPLATFORM_POSTMASTER,
      method: 'delete',
      uri: `/knowledge-base/${tenantId}/catalog/${catalogId}/document/${documentId}/version/${versionId}`,
    });
  }

  public static async getDocumentVersionContentAsync(
    tenantId: string,
    catalogId: string,
    documentId: string,
    versionId: string,
    type: string,
  ): Promise<string> {
    const result = await BlipApi.sendCommand({
      to: this.AIPLATFORM_POSTMASTER,
      method: 'get',
      uri: `/knowledge-base/${tenantId}/catalog/${catalogId}/document/${documentId}/version/${versionId}/content/${type}`,
    });

    return result.response.content;
  }

  //#endregion

  //#region Media

  public static async uploadFileAsync(file: File): Promise<string> {
    const uploadUri = await this.getUploadMediaUriAsync();

    // Convert file to binary
    const fileBinary = await getFileBinary(file);

    const response = await axios.post(uploadUri, fileBinary, {
      headers: {
        'Content-Type': file.type,
      },
    });

    return response.data.mediaUri;
  }

  public static async getUploadMediaUriAsync(): Promise<string> {
    const response = await BlipApi.sendCommand({
      to: 'postmaster@media.msging.net',
      method: 'get',
      uri: '/upload-media-uri?secure=true',
    });
    return response.response;
  }

  public static async refreshMediaExpiredLinksAsync(expiredMediaLinkUri: string): Promise<string> {
    const response = await BlipApi.sendCommand({
      to: 'postmaster@media.msging.net',
      method: 'set',
      uri: '/refresh-media-uri',
      type: 'text/plain',
      resource: expiredMediaLinkUri,
    });

    return response.response;
  }

  //#endregion
}
